<template>
  <div :class="transparent ? 'transparent-toolbar' : ''">
    <ToolbarContainer
      :hide-actions="showSearch"
      data-test-id="menu-toolbar"
      :class="[
        $style['toolbar-main'],
        { [$style['toolbar-main--show-search']]: !!showSearch }
      ]">
      <template #left>
        <a
          v-if="cartStore.shopToken"
          :class="$style['toolbar-main__logo']"
          :no-prefetch="true"
          :tabindex="isHidden ? -1 : 0"
          :href="`/${$lang}`">
          <LazyLogo />
        </a>
        <NuxtLink
          v-else
          :class="$style['toolbar-main__logo']"
          aria-label="Home"
          :no-prefetch="true"
          :tabindex="isHidden ? -1 : 0"
          :to="{ path: `/${$lang}` }">
          <LazyLogo />
        </NuxtLink>

        <SearchMain
          ref="search"
          :is-hidden="isHidden"
          data-test-id="form-search"
          :class="$style['toolbar-main__search']"
          aria-label="Search"
          @back="onCloseSearch" />
      </template>

      <template #right>
        <Btn
          aria-label="Search"
          class="h-hide-tablet-up"
          icon-only="System/search-line"
          color="white"
          :tabindex="isHidden ? -1 : 0"
          compact
          @click="toggleSearch" />

        <ClientOnly>
          <Btn
            aria-label="Wishlist"
            :tabindex="isHidden ? -1 : 0"
            :to="{ name: `${$lang}-wishlist` }"
            color="white"
            compact>
            <Badge size="small" :text="wishAmount">
              <SvgIcon name="Custom/heart-line-small" />
            </Badge>
          </Btn>
          <template #fallback>
            <Btn aria-label="Wishlist" :tabindex="isHidden ? -1 : 0" compact color="white">
              <Badge size="small">
                <SvgIcon
                  name="Custom/heart-line-small" />
              </Badge>
            </Btn>
          </template>
        </ClientOnly>

        <ClientOnly>
          <Btn
            title="Shopping bag"
            color="white"
            compact
            :tabindex="isHidden ? -1 : 0"
            :to="{ name: `${$lang}-cart` }"
            aria-label="Show cart">
            <Badge size="small" :text="cartStore.cartAmount">
              <SvgIcon name="Finance/shopping-cart-line" />
            </Badge>
          </Btn>
          <template #fallback>
            <Btn :tabindex="isHidden ? -1 : 0" aria-label="Show cart" compact color="white">
              <Badge size="small">
                <SvgIcon
                  name="Finance/shopping-cart-line" />
              </Badge>
            </Btn>
          </template>
        </ClientOnly>

        <ClientOnly>
          <Btn
            v-if="!rootStore.loginDisabled"
            aria-label="Account"
            data-test-id="account-button"
            role="button"
            color="white"
            compact
            class="h-hide-tablet-down"
            :to="{ name: `${$lang}-account-profile` }"
            :tabindex="isHidden ? -1 : 0">
            <Badge
              v-if="unreadNotifications === 0"
              :data-test-id="loggedIn ? 'account-button-badge-logged-in' : 'account-button-badge-logged-out'"
              :color="loggedIn ? 'success' : 'fail'"
              :icon="!loggedIn ? 'System/close-line' : 'System/check-line' "
              size="small">
              <SvgIcon name="User/account-circle-line" />
            </Badge>
            <Badge
              v-if="unreadNotifications > 0"
              size="small"
              :text="unreadNotifications">
              <SvgIcon name="User/account-circle-line" />
            </Badge>
          </Btn>
          <template #fallback>
            <Btn aria-label="Account" compact color="white" class="h-hide-tablet-down" :tabindex="isHidden ? -1 : 0">
              <Badge>
                <SvgIcon
                  name="User/account-circle-line" />
              </Badge>
            </Btn>
          </template>
        </ClientOnly>

        <Btn
          role="button"
          aria-label="Navigation"
          color="white"
          compact
          class="h-hide-tablet-up"
          :tabindex="isHidden ? -1 : 0"
          icon-only="System/menu-line"
          @keyup.enter="toggleDrawer()"
          @click="toggleDrawer()" />

        <Btn
          class="h-hide-tablet-down"
          color="white"
          compact
          role="button"
          aria-label="Select language"
          data-test-id="language-switcher-toggle"
          :tabindex="isHidden ? -1 : 0"
          :class="[$attrs.class]"
          @click="rootStore.toggleLanguageSwitcher()">
          <img
            :class="$style['image']"
            width="30"
            height="22.5"
            :alt="$lang"
            :src="languageImagePath($lang)">
        </Btn>
      </template>

      <template #middle>
        <ToolbarMainNavigation
          :is-hidden="isHidden"
          class="h-hide-tablet-down"
          @click="toggleDropdown(false)"
          @on-enter="toggleDropdown(true, $event)"
          @on-leave="toggleDropdown(false)" />
      </template>

      <template #bottom>
        <ToolbarDropdown
          :show="hoveredItemId"
          class="h-hide-tablet-down"
          :nav-id="hoveredItemId"
          @close="toggleDropdown(false)"
          @on-enter="toggleDropdown(true)"
          @on-leave="toggleDropdown(false)" />
      </template>
    </ToolbarContainer>
  </div>
</template>
<script setup>
import { debounce } from '~/helpers/debounce'

const rootStore = useRootStore()
const cartStore = useCartStore()
const authStore = useAuthStore()
const { notifications } = useNotifications()
const unreadNotifications = ref(0)
const wishlistStore = useWishlistStore()

const { toggleDrawer } = useDrawer()

defineProps({
  transparent: {
    type: Boolean,
    default: false
  },
  isHidden: {
    type: Boolean,
    default: false
  }
})

const showSearch = ref(false)
const search = ref(null)
const hoveredItemId = ref(null)

const wishAmount = computed(() => {
  return wishlistStore.items.length > 0
    ? wishlistStore.items.length
    : false
})
const loggedIn = computed(() => authStore.loggedIn)

const languageImagePath = lang => {
  return '/flags/' + (lang === 'en' ? 'eu' : lang) + '.svg'
}

const toggleSearch = async () => {
  showSearch.value = !showSearch.value
  if (search.value?.$el) {
    await nextTick()
    search.value?.$el.querySelector('input').focus()
  }
}

const toggleDropdown = debounce(function (show, itemId) {
  if (itemId) {
    hoveredItemId.value = itemId
  } else if (!show) {
    hoveredItemId.value = null
  }
}, 250)

const onCloseSearch = () => {
  showSearch.value = false
}

watch(notifications, () => {
  unreadNotifications.value = 0
  if (loggedIn.value) {
    notifications.value.forEach(notification => {
      if (!notification.readAt) {
        unreadNotifications.value++
      }
    })
  }
})

onMounted(async () => {
  await nextTick()
  const mediaQueryList = window.matchMedia('(min-width: 1350px)')
  mediaQueryList.addEventListener('change', onCloseSearch)
})

</script>
<style lang="scss" module>
.toolbar-main {
  :global(.transparent-toolbar):not(:hover) &::before {
    content: '';
    position: absolute;
    height: 0;
    width: 100%;
    top: $spacing * - 3.5;
    box-shadow: 0 86px 160px 41px rgb(0 0 0 / 60%);

    @media (max-width: #{$breakpoint-tablet - 1}) {
      box-shadow: 0 46px 100px 41px rgb(0 0 0 / 30%);
    }
  }

  @media (hover: none) and (max-width: #{$breakpoint-tablet - 1}) {
    :global(.transparent-toolbar) &::before {
      content: '';
      position: absolute;
      height: 0;
      width: 100%;
      top: $spacing * - 3.5;
      box-shadow: 0 46px 100px 41px rgb(0 0 0 / 30%);
    }
  }

  @media (pointer: coarse) and (max-width: #{$breakpoint-tablet - 1}) {
    :global(.transparent-toolbar) &::before {
      content: '';
      position: absolute;
      height: 0;
      width: 100%;
      top: $spacing * - 3.5;
      box-shadow: 0 46px 100px 41px rgb(0 0 0 / 30%);
    }
  }

  &__logo {
    color: inherit;
    align-items: center;
    display: flex;
    justify-content: center;
    margin-right: $spacing * 2;
    height: auto;
    width: $spacing * 18;
    transition: .3s;
    transform: translate(0, 0); // Fix for safari, force different css rendering

    @media (min-width: #{$breakpoint-tablet - 1}) {
      width: $spacing * 24;
      margin-right: $spacing * 4;
    }

    @media (min-width: #{$breakpoint-desktop}) {
      width: $spacing * 30;
    }

    :global(.transparent-toolbar):not(:hover) & {
      color: white;
    }

    @media (hover: none) and (max-width: #{$breakpoint-tablet - 1}) {
      :global(.transparent-toolbar) & {
        color: white;
      }
    }

    @media (pointer: coarse) and (max-width: #{$breakpoint-tablet - 1}) {
      :global(.transparent-toolbar) & {
        color: white;
      }
    }
  }

  &__search{
    display: none !important;
    width: 100%;
    z-index: (z-index(modal)) + 1;

    @media (min-width: #{$breakpoint-tablet}) {
      display: flex !important;
      margin-left: auto;
      width: $breakpoint-mobile;
    }
  }
}

@media (max-width: #{$breakpoint-tablet - 1}) {
  .toolbar-main.toolbar-main--show-search {
    z-index: z-index(modal) + 1;

    .toolbar-main__logo {
      display: none;
    }

    .toolbar-main__search {
      display: flex !important;
    }

    .toolbar__actions {
      display: none;
    }

    .close-button {
      display: inline-flex !important;
    }
  }
}

.avatar {
  background-color: $grey-lighter;
  background-size: cover;
  background-position: 50% 50%;
  object-fit: cover;
  border-radius: 100%;
  width: $spacing * 3;
  height: $spacing * 3;
  text-indent: -999px;
}
</style>
